import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useWindowDimensions } from "../../../utils";
import {
  PROGRAM_MAIN_PATH,
  PROGRAM_NODES,
} from "./programLevelLayout";
import { PROGRAM_MAP_MIN_WIDTH } from "./programLayout";
import { ProgramLevelNode } from "./ProgramLevelNode";
import {
  MapBackdrop,
  MapContainer,
  MapInner,
  MapScrollWrap,
  MapSvg,
} from "./ProgramMap.styles";

const isMainProgramLevel = (levelName) => {
  const value = Number(String(levelName).trim());
  return Number.isInteger(value) && value >= 0 && value <= 6;
};

const findTrialsLevel = (levels) =>
  levels.find((level) => String(level.level).trim().toLowerCase() === "trials")
  ?? levels.find((level) => String(level.level).trim().toLowerCase() === "kickoff")
  ?? levels.find((level) => !isMainProgramLevel(level.level));

export const ProgramMap = ({ levels = [] }) => {
  const navigate = useNavigate();
  const { width: screenWidth } = useWindowDimensions();
  const compact = screenWidth < PROGRAM_MAP_MIN_WIDTH;

  const levelByIndex = useMemo(() => {
    const map = {};
    levels.forEach((level) => {
      map[+level.level] = level;
    });
    return map;
  }, [levels]);

  const trialsLevel = useMemo(() => findTrialsLevel(levels), [levels]);

  const handleLevelClick = (levelIndex) => {
    const levelData = levelByIndex[levelIndex];
    if (levelData) {
      navigate(`/service/program/level/${levelData.id}`);
    }
  };

  const handleTrialsClick = () => {
    if (trialsLevel) {
      navigate(`/service/program/level/${trialsLevel.id}`);
    }
  };

  return (
    <MapScrollWrap>
      <MapContainer>
        <MapBackdrop />
        <MapInner>
          <MapSvg viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <filter id="program-path-shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0.35" stdDeviation="0.35" floodOpacity="0.18" />
              </filter>
            </defs>
            <path
              d={PROGRAM_MAIN_PATH}
              fill="none"
              stroke="#CBB8A1"
              strokeWidth="2.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#program-path-shadow)"
              opacity="0.92"
            />
            <path
              d={PROGRAM_MAIN_PATH}
              fill="none"
              stroke="#F5EDE2"
              strokeWidth="2.1"
              strokeDasharray="1.8 0.65"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </MapSvg>

          {PROGRAM_NODES.map((node) => {
            const isLevel = node.type === "level";
            const isTrials = node.type === "trials";
            const levelData = isLevel ? levelByIndex[node.levelIndex] : null;

            return (
              <ProgramLevelNode
                key={node.key}
                node={node}
                compact={compact}
                interactive={
                  (isTrials && Boolean(trialsLevel))
                  || (isLevel && Boolean(levelData))
                }
                onClick={
                  isTrials
                    ? handleTrialsClick
                    : isLevel
                      ? () => handleLevelClick(node.levelIndex)
                      : undefined
                }
              />
            );
          })}
        </MapInner>
      </MapContainer>
    </MapScrollWrap>
  );
};
